import React from 'react';
import { TargetIcon, GlobeIcon, SearchIcon, VideoIcon, StarIcon, DatabaseIcon, BotIcon } from 'lucide-react';
import { ScrollReveal } from './ScrollReveal';
const sections = [{
  number: '01',
  title: 'Brand Positioning',
  description: 'Clear positioning aligned to the commercial market.',
  items: ['Defined value proposition tailored to landlords and commercial clients', 'Clear explanation of services and who they are for', 'Messaging framework for commercial and residential', 'Structured explanation of installation and compliance process', 'Clear communication of Daikin partnership and Construction Gold accreditation', 'Competitive positioning away from price-led competition']
}, {
  number: '02',
  title: 'Full Website Rebuild',
  description: 'Built within the approved website structure and layout. This is a strategic refinement, not a redesign of the proposal format.',
  items: ['Strategic sitemap aligned to revenue priorities', 'Authority-led homepage', 'Dedicated service pages for commercial installations, maintenance and compliance, call outs and repairs, residential services, partnerships', 'Case study section', 'Testimonial integration', 'Accreditation and compliance visibility', 'Quote and emergency callout forms', 'Conversion tracking', 'Mobile optimisation', 'Speed optimisation']
}, {
  number: '03',
  title: 'Search Visibility Foundation',
  description: 'Structured to perform on Google and AI-powered search platforms.',
  items: ['Technical SEO setup', 'Local search optimisation', 'Service keyword targeting', 'Clear structured page hierarchy', 'Basic optimisation for AI visibility']
}, {
  number: '04',
  title: 'Video Authority System',
  description: '',
  items: ['Setup guidance for in-house video recording', 'Editing of 25 short videos', 'One month structured content plan', 'Direction for filming case studies', 'Before and after documentation framework'],
  note: 'Each video will have a dedicated landing page designed to improve ranking opportunities, support AI visibility and generate enquiries.'
}, {
  number: '05',
  title: 'Review & Case Study System',
  description: '',
  items: ['Review request process template', 'Scripts for requesting reviews', 'Testimonial capture structure', 'Case study template', 'Guidance for filming project walkthroughs']
}, {
  number: '06',
  title: 'CRM Integration',
  description: '',
  items: ['Integration with existing CRM', 'Structured enquiry forms', 'Lead tracking setup', 'Basic reporting structure']
}];
export function WhatsIncluded() {
  return <section id="included" className="py-20 md:py-28 px-4 md:px-6 bg-surface border-b border-white/[0.06]" data-id="element-330">
      <div className="max-w-7xl mx-auto" data-id="element-331">
        <ScrollReveal data-id="element-332">
          <div className="mb-12 md:mb-16" data-id="element-333">
            <span className="text-accent font-bold tracking-[0.2em] text-xs md:text-sm uppercase block mb-4 md:mb-6" data-id="element-334">
              Deliverables
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 md:mb-8 tracking-tight uppercase leading-[0.9]" data-id="element-335">
              What's
              <br data-id="element-336" />
              included
            </h2>
            <div className="bg-surface-elevated border border-white/[0.06] text-white p-6 md:p-8 max-w-2xl" data-id="element-337">
              <h3 className="text-lg md:text-xl font-bold leading-tight mb-4" data-id="element-338">
                6 week project to position Accutemp as the trusted commercial
                HVAC partner.
              </h3>
              <p className="text-zinc-400 text-sm md:text-base leading-relaxed" data-id="element-339">
                To position Accutemp as a trusted commercial HVAC partner for
                landlords, facilities managers and business owners, and to turn
                the website into a consistent source of qualified enquiries.
                This project strengthens authority, improves lead quality and
                reduces reliance on price-based competition.
              </p>
            </div>
          </div>
        </ScrollReveal>

        {/* Sections grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.06] border border-white/[0.06] mb-px" data-id="element-340">
          {sections.map((section, index) => <ScrollReveal key={section.number} delay={index * 0.06} data-id="element-341">
              <div className="bg-surface p-6 md:p-8 h-full" data-id="element-342">
                <div className="flex items-start gap-4 mb-5" data-id="element-343">
                  <span className="text-xs font-mono text-zinc-600 tracking-widest flex-shrink-0 mt-1" data-id="element-344">
                    [{section.number}]
                  </span>
                  <h3 className="text-base md:text-lg font-bold text-white uppercase tracking-tight" data-id="element-345">
                    {section.title}
                  </h3>
                </div>
                {section.description ? <p className="text-zinc-400 text-sm leading-relaxed mb-5 pl-8" data-id="element-346">
                    {section.description}
                  </p> : null}
                <ul className="space-y-2.5 pl-8" data-id="element-347">
                  {section.items.map((item, i) => <li key={i} className="flex items-start text-sm text-zinc-400" data-id="element-348">
                      <span className="mr-3 text-accent flex-shrink-0 mt-0.5" data-id="element-349">
                        +
                      </span>
                      {item}
                    </li>)}
                </ul>
                {section.note && <p className="mt-5 pl-8 text-xs text-zinc-500 leading-relaxed border-t border-white/[0.06] pt-4" data-id="element-350">
                    {section.note}
                  </p>}
              </div>
            </ScrollReveal>)}
        </div>

        {/* Partner Service Offer - AEO */}
        <ScrollReveal delay={0.3} data-id="element-351">
          <div className="border border-accent/30 bg-accent/5 p-6 md:p-8" data-id="element-352">
            <div className="flex flex-col md:flex-row md:items-start gap-6" data-id="element-353">
              <div className="flex-shrink-0" data-id="element-354">
                <div className="flex items-center gap-3 mb-1" data-id="element-355">
                  <BotIcon className="w-5 h-5 text-accent" data-id="element-356" />
                  <span className="text-accent font-bold text-xs uppercase tracking-widest" data-id="element-357">
                    Partner Service Offer Included
                  </span>
                </div>
                <p className="text-[10px] text-accent/60 font-mono uppercase tracking-widest" data-id="element-358">
                  Value £500 — included at no additional cost
                </p>
              </div>
              <div className="flex-1" data-id="element-359">
                <h3 className="text-lg md:text-xl font-bold text-white uppercase tracking-tight mb-3" data-id="element-360">
                  Answer Engine Optimisation (AEO)
                </h3>
                <p className="text-zinc-400 text-sm leading-relaxed mb-2" data-id="element-361">
                  AEO stands for Answer Engine Optimisation.
                </p>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6" data-id="element-362">
                  This prepares your website content so AI tools such as ChatGPT
                  and Google AI can clearly understand your services and
                  recommend Accutemp as a trusted provider.
                </p>
                <h4 className="text-[10px] font-bold text-zinc-500 tracking-[0.2em] uppercase mb-4" data-id="element-363">
                  Included
                </h4>
                <ul className="space-y-2.5 mb-6" data-id="element-364">
                  {['Review of service content for AI readiness', 'Structured landing page framework for educational content', 'Guidance on answering common HVAC questions', 'Content structure to support AI visibility', 'Implementation support'].map((item, i) => <li key={i} className="flex items-start text-sm text-zinc-400" data-id="element-365">
                      <span className="mr-3 text-accent flex-shrink-0" data-id="element-366">+</span>
                      {item}
                    </li>)}
                </ul>
                <p className="text-[11px] text-zinc-600 uppercase tracking-widest border-t border-white/[0.06] pt-4" data-id="element-367">
                  Supported by The AI Team and the Squidgy platform.
                </p>
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>;
}